/**
 * The twelve evergreen keyword glyphs. UI_UX section 4.
 *
 * Monoline, drawn on a 24x24 grid, stroked rather than filled so one set of
 * paths reads at every size from an 11px badge to a 28px help entry. Each entry
 * is a list of subpaths plus optional dots, because a few glyphs need a filled
 * accent (the drip, the runner ahead of the pack).
 *
 * Never branch on a card name to pick an icon: the key here is the Keyword
 * itself, which is what the engine and the card data both speak.
 */

import type { Keyword } from '@/engine';

export interface KeywordGlyph {
  /** Stroked subpaths. */
  d: string[];
  /** Filled circles, as [cx, cy, r]. */
  dots?: [number, number, number][];
  /** One-line definition. Used by tooltips and by HELP. */
  blurb: string;
  /** Display label in small caps. */
  label: string;
}

export const KEYWORD_GLYPHS: Record<Keyword, KeywordGlyph> = {
  vamp: {
    label: 'Vamp',
    blurb: 'Damage this deals also restores that much Integrity to its controller.',
    d: ['M12 3.5c3.4 4 5.6 6.7 5.6 9.4a5.6 5.6 0 0 1-11.2 0c0-2.7 2.2-5.4 5.6-9.4z'],
  },
  drainerLink: {
    label: 'Drainer Link',
    blurb: 'Any damage this deals to a character destroys it.',
    d: [
      'M9.6 9.1 8 10.7a3.4 3.4 0 0 0 4.8 4.8l1.6-1.6',
      'M14.4 14.9 16 13.3a3.4 3.4 0 0 0-4.8-4.8L9.6 10.1',
    ],
    dots: [[12, 20.2, 1.3]],
  },
  ape: {
    label: 'Ape',
    blurb: 'Can attack the turn it arrives.',
    d: ['M5 6.5 11 12l-6 5.5', 'M12 6.5 18 12l-6 5.5'],
  },
  offChain: {
    label: 'Off-Chain',
    blurb: 'Can only be blocked by Off-Chain or Oracle characters.',
    d: [
      'M7.5 17.5h9a3.5 3.5 0 0 0 .3-7 4.8 4.8 0 0 0-9.2-.6 3.6 3.6 0 0 0-.1 7.6z',
      'M12 15.5V8.4',
      'M9.6 10.6 12 8.2l2.4 2.4',
    ],
  },
  oracle: {
    label: 'Oracle',
    blurb: 'Can block Off-Chain characters.',
    d: ['M2.6 12S6 6.4 12 6.4 21.4 12 21.4 12 18 17.6 12 17.6 2.6 12 2.6 12z', 'M12 9.6a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 0 0 0-4.8z'],
  },
  coldStorage: {
    label: 'Cold Storage',
    blurb: 'Your opponent cannot target this.',
    d: [
      'M4.2 4.6h15.6v14.8H4.2z',
      'M12 8v8',
      'M8.5 10 12 12l3.5-2',
      'M8.5 14 12 12l3.5 2',
    ],
  },
  hodl: {
    label: 'HODL',
    blurb: 'Cannot be destroyed by damage or by destroy effects.',
    d: ['M12 3.4 20.6 12 12 20.6 3.4 12z', 'M7.6 12 12 7.6l4.4 4.4L12 16.4z'],
  },
  uptime: {
    label: 'Uptime',
    blurb: 'Attacking does not exhaust this.',
    d: ['M12 3.6a8.4 8.4 0 1 0 0 16.8 8.4 8.4 0 0 0 0-16.8z', 'M12 7.2V12l3.4 2'],
  },
  raid: {
    label: 'Raid',
    blurb: 'Cannot be blocked by fewer than two characters.',
    d: ['M3.4 5.6 10.6 12l-7.2 6.4', 'M20.6 5.6 13.4 12l7.2 6.4'],
  },
  slippage: {
    label: 'Slippage',
    blurb: 'Excess damage past its blockers hits the defending player.',
    d: ['M3.4 12h13.8', 'M13.2 7.8 17.6 12l-4.4 4.2', 'M20.6 5v14'],
  },
  frontrun: {
    label: 'Frontrun',
    blurb: 'Deals its combat damage before characters without Frontrun.',
    d: ['M8.6 12h11', 'M15.6 7.8 19.8 12l-4.2 4.2'],
    dots: [
      [3.6, 12, 1.5],
      [7, 12, 0.9],
    ],
  },
  staked: {
    label: 'Staked',
    blurb: 'Cannot attack.',
    d: ['M6.2 10.8h11.6v8.4H6.2z', 'M8.6 10.8V8a3.4 3.4 0 0 1 6.8 0v2.8'],
    dots: [[12, 15, 1.2]],
  },
};

/** Stable keyword order for HELP and for badge rows. */
export const KEYWORD_ORDER: Keyword[] = [
  'vamp',
  'drainerLink',
  'ape',
  'offChain',
  'oracle',
  'coldStorage',
  'hodl',
  'uptime',
  'raid',
  'slippage',
  'frontrun',
  'staked',
];

/** The /help anchor for a keyword, e.g. "/help#keyword-vamp". */
export function keywordAnchor(keyword: Keyword): string {
  return `/help#keyword-${keyword.toLowerCase()}`;
}
